import { useEffect, useMemo, useState } from 'react';
import {
  MagnifyingGlassIcon,
  PhoneIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { msalInstance } from '../auth/msalInstance';
import { apiRequest } from '../auth/authConfig';

// MFA migration call list — for the drive to move everyone off SMS onto the
// Authenticator app before month end. Data comes from the tech app's Graph-
// backed userRegistrationDetails (same source as the IT MFA progress card),
// joined to employee_mobile for a number to ring. IT-group gated server-side.
const API = `${process.env.REACT_APP_TECH_URL || '/it'}/api/mfa/call-list`;
const DONE_KEY = 'mfaCallList.done.v1';

// "Still on SMS or unprotected" — the people this page exists to work through.
const CALL_CATEGORIES = ['smsOnly', 'none', 'both'];

const CATEGORY = {
  smsOnly: { label: 'SMS only', tone: 'bg-red-50 text-red-700 ring-red-600/20', priority: true },
  none: { label: 'No MFA', tone: 'bg-orange-50 text-orange-700 ring-orange-600/20', priority: true },
  both: { label: 'App + SMS', tone: 'bg-amber-50 text-amber-800 ring-amber-600/20', priority: false },
  appOnly: { label: 'App only', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', priority: false },
  unknown: { label: 'No data', tone: 'bg-gray-100 text-gray-500 ring-gray-500/20', priority: false },
};

function useCallList(refreshKey) {
  const [state, setState] = useState({ data: null, loading: true, error: false });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      try {
        const account = msalInstance.getActiveAccount();
        const { accessToken } = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
        const res = await fetch(refreshKey ? `${API}?refresh=1` : API, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setState({ data: json, loading: false, error: false });
      } catch {
        if (!cancelled) setState({ data: null, loading: false, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  return state;
}

function StatCard({ label, value, tone, icon: Icon }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-gray-500">
        {Icon ? <Icon className="h-4 w-4" /> : null}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className={`mt-1 text-3xl font-bold tabular-nums ${tone || 'text-gray-900'}`}>{value}</div>
    </div>
  );
}

function MethodBadges({ hasApp, hasSms }) {
  return (
    <div className="flex flex-wrap gap-1">
      {hasApp && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
          <ShieldCheckIcon className="h-3 w-3" /> App
        </span>
      )}
      {hasSms && (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
          <DevicePhoneMobileIcon className="h-3 w-3" /> SMS
        </span>
      )}
      {!hasApp && !hasSms && <span className="text-xs text-gray-400">—</span>}
    </div>
  );
}

export default function MfaCallList() {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, loading, error } = useCallList(refreshKey);
  const [search, setSearch] = useState('');
  const [showDone, setShowDone] = useState(false); // include app-only (already migrated)
  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DONE_KEY) || '[]')); } catch { return new Set(); }
  });

  const toggleDone = (upn) => {
    setDone((prev) => {
      const next = new Set(prev);
      if (next.has(upn)) next.delete(upn); else next.add(upn);
      try { localStorage.setItem(DONE_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const counts = data?.counts || {};
  const users = useMemo(() => data?.users || [], [data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const inScope = showDone ? true : CALL_CATEGORIES.includes(u.category);
      if (!inScope) return false;
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q) || (u.upn || '').toLowerCase().includes(q) || (u.mobile || '').includes(q);
    });
  }, [users, search, showDone]);

  const callTotal = users.filter((u) => CALL_CATEGORIES.includes(u.category)).length;
  const callDone = users.filter((u) => CALL_CATEGORIES.includes(u.category) && done.has(u.upn)).length;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">MFA Migration — Call List</h1>
          <p className="mt-1 text-sm text-gray-500">
            Everyone still using SMS (or with no MFA yet) to move onto the Authenticator app.
            {data?.generatedAt ? (
              <span className="text-gray-400"> · data {new Date(data.generatedAt).toLocaleString('en-GB')}{data.cached ? ' (cached)' : ''}</span>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Couldn’t load the MFA data. You need IT-group access to view this page — if you’re sure you have it, try Refresh.
        </div>
      )}

      {loading && !data && <p className="mt-6 text-sm text-gray-400">Loading MFA registration data…</p>}

      {data && (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="SMS only" value={counts.smsOnly || 0} tone="text-red-600" icon={DevicePhoneMobileIcon} />
            <StatCard label="No MFA" value={counts.none || 0} tone="text-orange-600" icon={ExclamationTriangleIcon} />
            <StatCard label="App + SMS" value={counts.both || 0} tone="text-amber-600" icon={DevicePhoneMobileIcon} />
            <StatCard label="App only ✓" value={counts.appOnly || 0} tone="text-emerald-600" icon={CheckCircleIcon} />
          </div>

          <div className="mt-4 rounded-xl border border-brand/20 bg-brand-light px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-brand-dark">
                Called {callDone} of {callTotal} to migrate
              </span>
              <span className="text-xs text-brand-dark/70">{callTotal - callDone} left</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
              <div
                className="h-full rounded-full bg-brand transition-all"
                style={{ width: `${callTotal ? Math.round((callDone / callTotal) * 100) : 0}%` }}
              />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-emerald-600/25 bg-emerald-50/70 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-emerald-800">
                Switched to the app ✓{data.switched && data.switched.length ? ` · ${data.switched.length}` : ''}
              </span>
              <span className="text-xs text-emerald-700/70">since tracking started</span>
            </div>
            {data.switched && data.switched.length ? (
              <ul className="mt-2 divide-y divide-emerald-600/10">
                {data.switched.map((s) => (
                  <li key={s.upn || s.name} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-gray-800">{s.name}</span>
                    <span className="text-xs tabular-nums text-gray-500">
                      {new Date(s.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs text-emerald-700/80">
                No switches logged yet — as people are moved onto the app their name and the time will appear here. Tap Refresh to check for new ones.
              </p>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or number…"
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand" />
              Show app-only (done) too
            </label>
          </div>

          <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 w-10">Done</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Number</th>
                  <th className="px-4 py-2">Current MFA</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {visible.map((u) => {
                  const isDone = done.has(u.upn);
                  const cat = CATEGORY[u.category] || CATEGORY.unknown;
                  return (
                    <tr key={u.upn || u.name} className={isDone ? 'bg-gray-50/60 text-gray-400' : ''}>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={isDone}
                          onChange={() => toggleDone(u.upn)}
                          className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className={`font-medium ${isDone ? 'line-through' : 'text-gray-900'}`}>{u.name}</div>
                        {u.upn ? <div className="text-xs text-gray-400">{u.upn}</div> : null}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap tabular-nums">
                        {u.mobile ? (
                          <a href={`tel:${u.mobile.replace(/\s+/g, '')}`} className="inline-flex items-center gap-1.5 font-medium text-brand hover:text-brand-dark">
                            <PhoneIcon className="h-4 w-4" /> {u.mobile}
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400">no number on file</span>
                        )}
                      </td>
                      <td className="px-4 py-2"><MethodBadges hasApp={u.hasApp} hasSms={u.hasSms} /></td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cat.tone}`}>{cat.label}</span>
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">Nobody matches — everyone here is migrated. 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-gray-400">
            “Done” ticks are saved on this device only, so you can work through the list across the day. SMS-only and No-MFA are shown first.
          </p>
        </>
      )}
    </div>
  );
}
